/**
 * Every word a person reads instead of a code, with the tone it carries.
 *
 * Plain data — no React, no Prisma — so a client component, a server page and
 * the API name the same thing the same way. Per-screen copies had drifted:
 * `/account-invites` labelled account roles `OWNER`/`MEMBER` when the real
 * ones are `OWNER`/`MANAGER`/`VIEWER`, so those invites printed raw enums.
 *
 * Translation, when it comes, wraps `labelOf` here.
 */

import { CHANNEL_LADDER } from './api-types';
import { CONFIDENCE_HIGH, CONFIDENCE_MEDIUM } from './revenue-risk';
import type { ReferralRewardStatus, ReferralStatus } from './enums';
import type { ClientStep } from './client-journey';
import type { CustomerCaseStatus } from './customer-case-status';
import {
  BillingEventOutcome,
  BillingStatus,
  CaseCloseReason,
  CaseEventType,
  InviteStatus,
  InvoiceStatus,
  MatchConfidence,
  QuotaMetric,
  ScanMode,
  SpendReason,
  SubmissionStatus,
  SupportRequestType,
  SupportTicketStatus,
  ChannelThreadStatus,
  ErrorSource,
  NotificationKind,
  ScanRunStatus,
  SupportChannel,
  SupportThreadStatus,
  CLOSED_CASE_STATUSES,
  OPEN_CASE_STATUSES,
  DomainCaseStatus,
  DomainTrustLevel,
  PhotoMatchState,
  PlanCode,
} from './enums';

/**
 * What a state means, not what it looks like. `chip-*` and `tone-*` in
 * globals.css are the only places these get a colour, so one CSS edit moves
 * every chip, dot and marker that reads the same tone.
 */
export type Tone = 'neutral' | 'progress' | 'resolved' | 'attention' | 'danger';

/** One entry: what a person reads, and how loudly. */
export interface Term {
  label: string;
  tone: Tone;
}

/** A vocabulary is a partial map: an unknown code falls back to `humanize`. */
export type Vocabulary = Readonly<Record<string, Term>>;

/**
 * An unmapped code, made readable. Screens each had their own
 * `replace(/_/g, ' ')`, which left a lowercase word beside capitalised ones.
 */
export function humanize(code: string): string {
  const spaced = code.replace(/[_-]+/g, ' ').trim();
  return spaced ? spaced[0].toUpperCase() + spaced.slice(1) : code;
}

/** The term for a code, or a humanized fallback that never throws. */
export function termOf(vocabulary: Vocabulary, code: string | null | undefined): Term {
  if (!code) return { label: '—', tone: 'neutral' };
  return vocabulary[code] ?? { label: humanize(code), tone: 'neutral' };
}

/** Just the words, for a cell that carries no chip. */
export function labelOf(vocabulary: Vocabulary, code: string | null | undefined): string {
  return termOf(vocabulary, code).label;
}

/** Reads a plain label table with the same fallback, for maps with no tone. */
export function plainLabel(labels: Readonly<Record<string, string>>, code: string | null | undefined): string {
  if (!code) return '—';
  return labels[code] ?? humanize(code);
}

/* ── takedown cases ──────────────────────────────────────────────── */

/**
 * A domain case's state, keyed by the enum, so a new status forgotten here is
 * a type error and not a raw code on the panel.
 *
 * The words are the statuses' own — Artur, 2026-09-04. They used to be
 * friendlier ("Removed", "Couldn't remove"), which read better in a sentence
 * and worse everywhere it mattered: a column headed "Removed" over cases
 * whose status is `done`, and a filter offering a word the team does not use
 * when it talks about the case. The client app says it its own way (see the
 * submit page), because she is not reading a board.
 */
export const DOMAIN_CASE_STATUS: Readonly<Record<DomainCaseStatus, Term>> = {
  [DomainCaseStatus.PENDING]: { label: 'New', tone: 'attention' },
  [DomainCaseStatus.HOLD]: { label: 'Hold', tone: 'attention' },
  [DomainCaseStatus.IN_PROGRESS]: { label: 'In progress', tone: 'progress' },
  [DomainCaseStatus.DONE]: { label: 'Done', tone: 'resolved' },
  [DomainCaseStatus.DECLINED]: { label: 'Declined', tone: 'danger' },
  [DomainCaseStatus.CANCELLED]: { label: 'Cancelled', tone: 'neutral' },
};

/**
 * The six states a case can be in, in the order work moves through them —
 * and, since 2026-09-04, the order of the board's columns.
 *
 * There used to be a second vocabulary here: four reader-facing words over
 * seven database states, because nobody outside asked for `in_review` as
 * opposed to `in_progress`. Artur removed the difference itself instead —
 * `in_review` is gone, "Take" lands a case straight in `in_progress` — so the
 * two vocabularies are one, and a state's word is now the same on the board,
 * in a filter and in a report by construction rather than by a mapping table
 * somebody has to keep honest.
 *
 * `pending` reads as "New" to a person (see {@link DOMAIN_CASE_STATUS}); the
 * code keeps the database's own word, so there is exactly one place where the
 * two are tied together.
 */
export const CASE_STATUS_COLUMNS: readonly DomainCaseStatus[] = [
  DomainCaseStatus.PENDING,
  DomainCaseStatus.IN_PROGRESS,
  DomainCaseStatus.HOLD,
  DomainCaseStatus.DECLINED,
  DomainCaseStatus.DONE,
  DomainCaseStatus.CANCELLED,
];

/**
 * The board in two halves, and one button each — Artur, 2026-09-04.
 *
 * Six status columns at once left every column 180px wide, and two of the six
 * (`done`, `cancelled`) grow without limit and are never worked: a third of
 * the screen holding an archive. Split this way each half gets three columns
 * of real width, and which half you are in is one click rather than a filter
 * composed out of six statuses.
 *
 * Here rather than on either screen because both the console and Activity
 * draw it, and a board whose "Active" means one thing on one screen and
 * another on the next is worse than no button at all.
 */
export const CASE_BOARD_HALVES = [
  { key: 'active', label: 'Active', statuses: OPEN_CASE_STATUSES.map(String) },
  { key: 'archive', label: 'Archive', statuses: CLOSED_CASE_STATUSES.map(String) },
  /*
    And the whole board, asked for by Artur 2026-09-09: a question like "has
    this domain ever been filed" spans both halves, and answering it by
    clicking Active, reading, clicking Archive, reading again is how a case
    gets missed. Six columns do not fit at 1fr each, so the board scrolls
    sideways at a readable width on this half only (`.ly-board.is-wide`).
  */
  {
    key: 'all',
    label: 'All cases',
    statuses: [...OPEN_CASE_STATUSES.map(String), ...CLOSED_CASE_STATUSES.map(String)],
  },
] as const;

export type CaseBoardHalf = (typeof CASE_BOARD_HALVES)[number]['key'];

/** Where the reader's choice of half is remembered. One key, so the console
 *  and Activity open on the same half rather than disagreeing about where
 *  somebody was working. */
export const CASE_BOARD_HALF_KEY = 'activity-half';

/**
 * The reasons a case wants a person, as the board's own quick filters.
 *
 * Artur, 2026-09-04: one line above the board — "3 new replies · 2 bounced · 4
 * silent 3 days" — where every number narrows the board to those cases. Not a
 * dashboard and not statistics: these are the ways a morning starts, and the
 * number is the button.
 *
 * Each is a `where` the API owns (see `TakedownService.whereForReason`), so a
 * count and the board under it cannot disagree — the same clause answers both.
 */
export const ATTENTION_REASONS = [
  /* The tone is what the number is coloured with in the counters group: news
     from outside is the attention colour, a failure is the danger one, and
     our own backlog is plain ink. Colour on the figure alone, so the row of
     words under it stays quiet. */
  { key: 'replies', label: 'new replies', hint: 'A host answered on some channel.', tone: 'attention' },
  { key: 'bounced', label: 'bounced', hint: 'A notice never arrived. The address needs fixing.', tone: 'danger' },
  { key: 'silent', label: 'silent 3 days', hint: 'Sent, and nothing came back in three days.', tone: 'plain' },
  { key: 'removed', label: 'links removed', hint: 'A check found links gone and nobody has looked.', tone: 'resolved' },
  { key: 'drafts', label: 'drafted, not sent', hint: 'A notice is written and waiting to be sent.', tone: 'plain' },
] as const;

export type AttentionReason = (typeof ATTENTION_REASONS)[number]['key'];

/** What each state means, one line, for the hint on a filter or a column. */
export const CASE_STATUS_HINT: Readonly<Record<DomainCaseStatus, string>> = {
  [DomainCaseStatus.PENDING]: 'waiting for somebody to take it',
  [DomainCaseStatus.IN_PROGRESS]: 'taken, being worked',
  [DomainCaseStatus.HOLD]: 'taken, then stopped on purpose',
  [DomainCaseStatus.DECLINED]: 'no channel could remove the links',
  [DomainCaseStatus.DONE]: 'the links are gone',
  [DomainCaseStatus.CANCELLED]: 'the complaint was wrong; the content is legitimate',
};


/**
 * What kind of site a domain is. `pirate` is not an alarm — it is the ordinary
 * case and the one we act on fastest — so it reads as attention rather than
 * danger; `legitimate` is the one that should make somebody slow down, which
 * is why it is not green.
 */
export const DOMAIN_TRUST: Readonly<Record<DomainTrustLevel, Term>> = {
  [DomainTrustLevel.PIRATE]: { label: 'Pirate site', tone: 'attention' },
  [DomainTrustLevel.SUSPICIOUS]: { label: 'Unjudged', tone: 'neutral' },
  [DomainTrustLevel.LEGITIMATE]: { label: 'Legitimate site', tone: 'progress' },
};

/**
 * Whether her photos confirm a finding. `no_photo` is neutral on purpose: it
 * says nothing about the finding, only about what we had to compare with, and
 * colouring it would be the same mistake as scoring it zero.
 */
export const PHOTO_MATCH: Readonly<Record<PhotoMatchState, Term>> = {
  [PhotoMatchState.NO_PHOTO]: { label: 'No photo to compare', tone: 'neutral' },
  [PhotoMatchState.MATCHES]: { label: 'Photo matches', tone: 'resolved' },
  [PhotoMatchState.NO_MATCH]: { label: 'Photo does not match', tone: 'danger' },
};

/**
 * The same nine statuses in the shortest words that still read, for the board
 * — where every channel of every case shows its own status and there are six
 * of them across a column two hundred pixels wide. Only shortened where the
 * long word does not fit: `sent`, `draft`, `done` and `declined` are already
 * as short as they are true.
 */
export const CHANNEL_THREAD_SHORT: Readonly<Record<ChannelThreadStatus, string>> = {
  [ChannelThreadStatus.NOT_APPLICABLE]: 'n/a',
  [ChannelThreadStatus.DRAFT]: 'draft',
  [ChannelThreadStatus.SENT]: 'sent',
  [ChannelThreadStatus.BOUNCED]: 'bounced',
  [ChannelThreadStatus.ACKNOWLEDGED]: 'ack',
  [ChannelThreadStatus.NEW_REPLY]: 'new reply',
  [ChannelThreadStatus.NO_ANSWER]: 'no answer',
  [ChannelThreadStatus.DECLINED]: 'declined',
  [ChannelThreadStatus.DONE]: 'done',
};

/**
 * The channel's name at its shortest, for the row-of-boxes shape where six
 * cells share a 370px column and "REGISTRAR" alone is wider than the cell.
 * Only used there; the stacked shape has room for the real words.
 */
export const CHANNEL_TINY: Readonly<Record<string, string>> = {
  domain_email: 'mail',
  hoster_abuse: 'host',
  registrar_abuse: 'reg',
  cdn_abuse: 'cdn',
  search_delisting: 'delist',
  dmca: 'dmca',
};

/** The channel's own name, shortened the same way and for the same reason. */
export const CHANNEL_SHORT: Readonly<Record<string, string>> = {
  domain_email: 'email',
  hoster_abuse: 'hosting',
  registrar_abuse: 'registrar',
  cdn_abuse: 'cdn',
  search_delisting: 'delisting',
  dmca: 'dmca',
};

/** One channel thread's state, in the words the console shows. */
export const CHANNEL_THREAD_STATUS: Readonly<Record<ChannelThreadStatus, Term>> = {
  [ChannelThreadStatus.NOT_APPLICABLE]: { label: 'Not applicable', tone: 'neutral' },
  [ChannelThreadStatus.DRAFT]: { label: 'Drafted', tone: 'neutral' },
  [ChannelThreadStatus.SENT]: { label: 'Sent', tone: 'progress' },
  [ChannelThreadStatus.BOUNCED]: { label: 'Bounced', tone: 'attention' },
  [ChannelThreadStatus.ACKNOWLEDGED]: { label: 'Ticket acknowledged', tone: 'progress' },
  [ChannelThreadStatus.NEW_REPLY]: { label: 'New reply', tone: 'attention' },
  [ChannelThreadStatus.NO_ANSWER]: { label: 'No answer', tone: 'attention' },
  [ChannelThreadStatus.DECLINED]: { label: 'Declined', tone: 'danger' },
  // "Done" is the word support says out loud for the only outcome that
  // matters, so it says what actually happened rather than that the thread
  // reached its end.
  [ChannelThreadStatus.DONE]: { label: 'Content removed', tone: 'resolved' },
};

/** Why support closed a case, in the close modal's own words. */
export const CASE_CLOSE_REASON: Readonly<Record<CaseCloseReason, string>> = {
  [CaseCloseReason.ALL_REMOVED]: 'Removed — nothing is up any more',
  [CaseCloseReason.GONE_BEFORE_FILING]: 'Removed — gone before any notice was sent',
  [CaseCloseReason.ALL_DECLINED]: "Couldn't remove — every channel refused",
  [CaseCloseReason.INVALID]: 'Invalid — the complaint turned out wrong',
  [CaseCloseReason.OTHER]: 'Other',
};

/**
 * Audit-log verbs, not case states. A few spellings coincide (`sent`,
 * `declined`) and read fine through the status chip by accident; most don't,
 * which is how `/case-activity` printed `evidence_capture_failed` raw.
 */
export const CASE_EVENT: Readonly<Record<CaseEventType, Term>> = {
  opened: { label: 'Case opened', tone: 'neutral' },
  /* Also what a host's reply to a closed case does: the system puts it back in
     progress, the one thing here it decides by itself, because a reply nobody
     can see is a reply nobody answers (Artur, 2026-09-04). */
  reopened: { label: 'Reopened', tone: 'attention' },
  taken: { label: 'Taken in review', tone: 'progress' },
  held: { label: 'Put on hold', tone: 'attention' },
  unheld: { label: 'Returned to the queue', tone: 'progress' },
  sent: { label: 'Notice sent', tone: 'progress' },
  send_failed: { label: 'Could not send — no contact resolved', tone: 'attention' },
  reply: { label: 'Reply logged', tone: 'progress' },
  bounced: { label: 'Notice bounced', tone: 'attention' },
  late_reply: { label: 'Reply arrived after the channel closed', tone: 'attention' },
  acknowledged: { label: 'Auto-acknowledged', tone: 'neutral' },
  counter_notice: { label: 'Counter-notice received', tone: 'danger' },
  declined: { label: 'Channel declined', tone: 'neutral' },
  channel_done: { label: 'Content removed on this channel', tone: 'resolved' },
  channel_closed: { label: 'Channel closed — nothing more to do', tone: 'neutral' },
  // Not "case closed": every link reads as gone, which is the moment to
  // offer closing it, not to close it. See TakedownService.maybeRecalc.
  all_removed: { label: 'Every link now reads as gone', tone: 'resolved' },
  contact_added: { label: 'Contact added', tone: 'neutral' },
  whois_refreshed: { label: 'WHOIS refreshed', tone: 'neutral' },
  evidence_captured: { label: 'Evidence captured', tone: 'neutral' },
  evidence_capture_failed: { label: 'Evidence capture failed', tone: 'attention' },
  closed: { label: 'Case closed', tone: 'neutral' },
  assigned: { label: 'Reassigned', tone: 'neutral' },
  // A client changing hands is what makes a case somebody else's, so it is
  // written into the story of every open case it moved.
  handed_over: { label: 'Client handed to another support member', tone: 'neutral' },
  covered: { label: 'Client covered by a stand-in', tone: 'progress' },
  handed_back: { label: 'Client handed back', tone: 'progress' },
  // Short on purpose: a supervisor reads a dozen of these a day as a chip,
  // not a sentence. The explanation ("new finds open the next round",
  // "in three days") is still true and still in this file's own history —
  // just not owed to every glance at the case list any more.
  collection_stopped: { label: 'Collection stopped', tone: 'neutral' },
  merged: { label: 'Regrouped onto the site', tone: 'neutral' },
  returned: { label: 'Back up after being removed', tone: 'danger' },
  no_answer: { label: 'No answer', tone: 'attention' },
  url_added: { label: 'Link added to this case', tone: 'neutral' },
  split: { label: 'Live links moved to a new case', tone: 'attention' },
  new_round: { label: 'New round opened', tone: 'attention' },
};

/* ── accounts and invitations ────────────────────────────────────── */

/** An invitation's own life, on `/account-invites`. */
export const INVITE_STATUS: Readonly<Record<InviteStatus, Term>> = {
  pending: { label: 'Pending', tone: 'neutral' },
  accepted: { label: 'Accepted', tone: 'resolved' },
  revoked: { label: 'Revoked', tone: 'danger' },
  expired: { label: 'Expired', tone: 'attention' },
};

/* ── a removal, as the client reads it ──────────────────────────── */

/** The client's steps (DEC-001). "Processing" covers everything between
 *  finding a page and a result, which is exactly what it should say. */
export const CLIENT_STEP: Readonly<Record<ClientStep, Term>> = {
  found: { label: 'Found', tone: 'neutral' },
  processing: { label: 'Processing', tone: 'progress' },
  delisting_requested: { label: 'Delisting requested', tone: 'progress' },
  removed: { label: 'Removed', tone: 'resolved' },
  watching: { label: 'Watching', tone: 'resolved' },
  back: { label: 'Back', tone: 'attention' },
  filed_again: { label: 'Filed again', tone: 'progress' },
  whitelisted: { label: 'Whitelisted', tone: 'resolved' },
};

/** A case's status on her Removals screens: `customerCaseStatus` decides
 *  which one, this only names it. Hold is the panel's word, in a neutral tone. */
export const CUSTOMER_CASE_STATUS: Readonly<Record<CustomerCaseStatus, Term>> = {
  received: { label: 'Received', tone: 'neutral' },
  in_progress: { label: 'In progress', tone: 'progress' },
  hold: { label: 'Hold', tone: 'neutral' },
  removed: { label: 'Removed', tone: 'resolved' },
  unable_to_remove: { label: 'Unable to remove', tone: 'danger' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
};

/* ── referrals ───────────────────────────────────────────────────── */

/** An invitation's own life, on `/referrals` in both apps. */
export const REFERRAL_STATUS: Readonly<Record<ReferralStatus, Term>> = {
  SIGNED_UP: { label: 'Signed up', tone: 'neutral' },
  PAYING: { label: 'Paying', tone: 'resolved' },
  BLOCKED: { label: 'Blocked', tone: 'danger' },
};

/** Where a reward is. "Holding" is the refund window, not a problem. */
export const REFERRAL_REWARD_STATUS: Readonly<Record<ReferralRewardStatus, Term>> = {
  HOLDING: { label: 'Holding', tone: 'attention' },
  CREDITED: { label: 'Credited', tone: 'resolved' },
  REVERSED: { label: 'Reversed', tone: 'danger' },
};

/* ── billing ─────────────────────────────────────────────────────── */

/** What a Stripe webhook meant. Our outcome names and Stripe's own statuses
 *  in one table on purpose: they share a column and a reader. */
export const BILLING_OUTCOME: Readonly<Record<BillingEventOutcome | BillingStatus, Term>> = {
  /**
   * Stripe has never been asked about this account: it registered, it is on
   * the free tier, and there is no subscription to have a status. The raw
   * `none` it is stored as read on the clients directory as a fault — an
   * account whose billing had somehow gone missing — which is the opposite of
   * what it says.
   */
  none: { label: 'No subscription', tone: 'neutral' },
  checkout_completed: { label: 'Checkout completed', tone: 'resolved' },
  extra_username_purchased: { label: 'Extra username purchased', tone: 'resolved' },
  renewal_paid: { label: 'Renewal paid', tone: 'resolved' },
  invoice_paid: { label: 'Invoice paid', tone: 'resolved' },
  active: { label: 'Active', tone: 'resolved' },
  trialing: { label: 'Trialing', tone: 'resolved' },
  payment_failed: { label: 'Payment failed', tone: 'danger' },
  disputed: { label: 'Disputed', tone: 'danger' },
  refunded: { label: 'Refunded', tone: 'attention' },
  canceled: { label: 'Canceled', tone: 'danger' },
  unpaid: { label: 'Unpaid', tone: 'danger' },
  // One word, because it sits in a chip in a column: "Past due" wrapped or
  // pushed the lane wider than every other plan word on the roster.
  // Red, not orange: an unpaid renewal is money not arriving, which is the
  // same kind of fact as "unpaid" two lines up — orange read as a warning
  // about the work rather than about the account.
  past_due: { label: 'Overdue', tone: 'danger' },
  incomplete_expired: { label: 'Incomplete, expired', tone: 'danger' },
  incomplete: { label: 'Incomplete', tone: 'attention' },
  paused: { label: 'Paused', tone: 'attention' },
  unhandled: { label: 'Unhandled event', tone: 'neutral' },
  unmatched: { label: 'No account matched', tone: 'attention' },
};

/** Stripe's own event names in the words a colleague uses: the raw name is
 *  what the Stripe dashboard is searched by, so it stays in a tooltip rather
 *  than in the column somebody reads to answer "why did her plan reset". */
const STRIPE_EVENT: Readonly<Record<string, string>> = {
  // One or two words: these are chips in a column read top to bottom, and
  // "Invoice paid" on forty rows says "invoice" forty times to make one row
  // that says "Failed" harder to find.
  'checkout.session.completed': 'Checkout',
  'customer.created': 'Client added',
  'customer.updated': 'Client changed',
  'customer.deleted': 'Client removed',
  'customer.subscription.created': 'Plan started',
  'customer.subscription.updated': 'Plan changed',
  'customer.subscription.deleted': 'Plan ended',
  'customer.subscription.trial_will_end': 'Trial ending',
  'invoice.paid': 'Paid',
  'invoice.payment_succeeded': 'Paid',
  'invoice.payment_failed': 'Failed',
  'invoice.upcoming': 'Invoice due',
  'invoice.finalized': 'Invoice issued',
  'charge.refunded': 'Refunded',
  'charge.dispute.created': 'Disputed',
  'charge.dispute.closed': 'Dispute closed',
  'payment_intent.succeeded': 'Paid',
  'payment_intent.payment_failed': 'Failed',
  'coupon.created': 'Coupon added',
  'coupon.deleted': 'Coupon removed',
  'price.created': 'Price added',
  'product.updated': 'Product changed',
};

/**
 * What an event means for the money, said in the five tones the panel already
 * has: took money, lost money, gave money back, changed the subscription, or
 * changed nothing anybody is owed. A colour per event type would be a chart;
 * this is a column somebody reads for the one row that went wrong.
 */
export function stripeEventTone(type: string): Tone {
  if (/payment_failed|dispute\.created|\.deleted$/.test(type)) return 'danger';
  if (/refund|dispute\.closed|trial_will_end|past_due/.test(type)) return 'attention';
  if (/paid|payment_succeeded|checkout\.session\.completed/.test(type)) return 'resolved';
  // A client changed is a change like a plan changed, and not like a client
  // added, which stays quiet: the two read as one colour in the Event lane.
  if (/^customer\.(subscription\.|updated$)/.test(type)) return 'progress';
  return 'neutral';
}

/** What an event is called here. One this table has never heard of is still
 *  made readable: Stripe adds types without asking, and a screen that says
 *  `payment_method.automatically_updated` has given up on its reader. */
export function stripeEventLabel(type: string): string {
  const known = STRIPE_EVENT[type];
  if (known) return known;
  const words = type.replace(/[._]/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : type;
}

/**
 * A Stripe invoice's own status, on a client's own billing history. Apart from
 * BILLING_OUTCOME above, which names what a webhook meant to the account: this
 * names one document, in the words printed on it.
 */
export const INVOICE_STATUS: Readonly<Record<InvoiceStatus, Term>> = {
  paid: { label: 'Paid', tone: 'resolved' },
  open: { label: 'Due', tone: 'attention' },
  draft: { label: 'Draft', tone: 'neutral' },
  void: { label: 'Void', tone: 'neutral' },
  uncollectible: { label: 'Unpaid', tone: 'danger' },
};

/**
 * The product's name for a plan, as a client reads it. The retired codes are
 * named for what they are served as now (see LEGACY_PLAN).
 */
export const PLAN_NAME: Readonly<Record<PlanCode, string>> = {
  [PlanCode.ESSENTIAL]: 'Essential',
  [PlanCode.PRO]: 'Pro',
  [PlanCode.PREMIUM]: 'Premium',
  [PlanCode.AGENCY_STARTER]: 'Starter',
  [PlanCode.AGENCY_GROWTH]: 'Growth',
  [PlanCode.AGENCY_SCALE]: 'Scale',
  [PlanCode.PROTECTION]: 'Pro',
  [PlanCode.SIXTYNINE]: 'Pro',
  [PlanCode.AFTERGLOW]: 'Premium',
};

/** Who the plan is for, from the price list. */
export const PLAN_SUMMARY: Readonly<Record<PlanCode, string>> = {
  [PlanCode.ESSENTIAL]: 'Occasional leaks, one profile',
  [PlanCode.PRO]: 'Established creators',
  [PlanCode.PREMIUM]: 'High-volume creators',
  [PlanCode.AGENCY_STARTER]: 'Min 10 profiles',
  [PlanCode.AGENCY_GROWTH]: 'Min 30 profiles',
  [PlanCode.AGENCY_SCALE]: '100+ profiles',
  [PlanCode.PROTECTION]: 'Established creators',
  [PlanCode.SIXTYNINE]: 'Established creators',
  [PlanCode.AFTERGLOW]: 'High-volume creators',
};

/**
 * What each agency plan includes. A creator plan's list is its card in Settings
 * (`plan.<name>_card`), which the plan page and the marketing site both read.
 */
export const PLAN_FEATURES: Readonly<Partial<Record<PlanCode, { has: string[]; no?: string[] }>>> = {
  [PlanCode.AGENCY_STARTER]: {
    has: ['Portfolio dashboard', 'CSV bulk onboarding', 'Consolidated monthly report', 'Auto-mode content cleaning'],
  },
  [PlanCode.AGENCY_GROWTH]: {
    has: ['Advanced reporting & roles', 'Creator assignments', 'White-label PDF reports', 'Priority support'],
  },
  [PlanCode.AGENCY_SCALE]: {
    has: ['Usage-based enforcement', 'API access & webhooks', 'Dedicated Customer Success', 'Custom SLA + onboarding'],
  },
};

/** Every plan includes this, said once above the plans. */
export const PLANS_INCLUDE = 'Every plan includes human-verified enforcement and evidence reporting.';

/* ── scanning ────────────────────────────────────────────────────── */

/** A `ScanRun`'s state, on `/scans` and on a client's own dashboard. */
export const SCAN_STATUS: Readonly<Record<ScanRunStatus, Term>> = {
  running: { label: 'Running', tone: 'progress' },
  complete: { label: 'Complete', tone: 'resolved' },
  failed: { label: 'Failed', tone: 'danger' },
};

/** Why a scan ran. */
export const SCAN_MODE: Readonly<Record<ScanMode, Term>> = {
  scheduled: { label: 'Scheduled', tone: 'neutral' },
  live: { label: 'Live', tone: 'progress' },
  first: { label: 'First scan', tone: 'progress' },
};

/**
 * How sure the image match is, with the number spelled out. The keys are the
 * wire spelling the findings filter sends, and the numbers come from the same
 * constants the query uses -- the label said 75% while the query asked 70%.
 */
export const MATCH_CONFIDENCE: Readonly<Record<MatchConfidence, Term>> = {
  high: { label: `High (${Math.round(CONFIDENCE_HIGH * 100)}%+)`, tone: 'resolved' },
  medium: {
    label: `Medium (${Math.round(CONFIDENCE_MEDIUM * 100)}–${Math.round(CONFIDENCE_HIGH * 100) - 1}%)`,
    tone: 'progress',
  },
  low: { label: `Lower (<${Math.round(CONFIDENCE_MEDIUM * 100)}%)`, tone: 'attention' },
  unchecked: { label: 'Not checked', tone: 'neutral' },
};

/* ── support and operations ──────────────────────────────────────── */

/** Where a support thread arrived from. */
export const THREAD_CHANNEL: Readonly<Record<SupportChannel, Term>> = {
  whatsapp: { label: 'WhatsApp', tone: 'neutral' },
  web: { label: 'Web chat', tone: 'neutral' },
};

/** Whether a support thread is still being answered. */
export const THREAD_STATUS: Readonly<Record<SupportThreadStatus, Term>> = {
  open: { label: 'Open', tone: 'progress' },
  closed: { label: 'Closed', tone: 'resolved' },
};

/** Which part raised an error. Listing what only a browser can raise is the
 *  stable side of the split: a new backend source needs no edit here. */
export const ERROR_SOURCE: Readonly<Record<ErrorSource, Term>> = {
  api: { label: 'API 5xx', tone: 'danger' },
  cron: { label: 'Cron sweep', tone: 'attention' },
  jobs: { label: 'Queue job', tone: 'attention' },
  render: { label: 'Render', tone: 'danger' },
  error: { label: 'JS error', tone: 'danger' },
  unhandledrejection: { label: 'Promise', tone: 'attention' },
};

/** The three sources above that only a browser can raise. */
export const BROWSER_ERROR_SOURCES: readonly ErrorSource[] = [
  ErrorSource.RENDER,
  ErrorSource.ERROR,
  ErrorSource.UNHANDLEDREJECTION,
];

/** What a notification was about. `finding` is neutral though it is the bad
 *  news: it is most of the log, and a column of orange stops meaning
 *  anything. Only `quota`, which is about to block her, is loud. */
export const NOTIFICATION_KIND: Readonly<Record<NotificationKind, Term>> = {
  finding: { label: 'New finding', tone: 'neutral' },
  weekly_digest: { label: 'Weekly digest', tone: 'neutral' },
  case_resolved: { label: 'Case resolved', tone: 'resolved' },
  authorization: { label: 'Authorization', tone: 'neutral' },
  quota: { label: 'Quota warning', tone: 'attention' },
  setup: { label: 'Finish your setup', tone: 'attention' },
  scan_completed: { label: 'Scan completed', tone: 'neutral' },
  scan_failed: { label: 'Scan failed', tone: 'attention' },
  case_opened: { label: 'Removal submitted', tone: 'progress' },
  case_update: { label: 'Removal update', tone: 'progress' },
  support_reply: { label: 'Support replied', tone: 'attention' },
  support_update: { label: 'Ticket update', tone: 'neutral' },
};

/** A Help ticket's status, worded for the client, everywhere it is shown. */
export const TICKET_STATUS: Readonly<Record<SupportTicketStatus, Term & { hint: string }>> = {
  open: { label: 'Open', tone: 'neutral', hint: 'Waiting for the support team to pick it up.' },
  in_progress: { label: 'In progress', tone: 'progress', hint: 'The support team is working on it.' },
  waiting_client: { label: 'Waiting for you', tone: 'attention', hint: 'Support needs something from you to carry on.' },
  resolved: { label: 'Resolved', tone: 'resolved', hint: 'Support considers this sorted. Reply to reopen it.' },
  closed: { label: 'Closed', tone: 'neutral', hint: 'This ticket is closed. Open a new one if you need more help.' },
};

/** The "What do you need help with?" options, in the order the form lists them. */
export const REQUEST_TYPE: Readonly<Record<SupportRequestType, Term & { hint: string }>> = {
  account: { label: 'Account & Profile', tone: 'neutral', hint: 'Sign-in, team members, profile details' },
  scans: { label: 'Scans & Detections', tone: 'neutral', hint: 'What was found, or what was missed' },
  case_issue: { label: 'Case-related issue', tone: 'neutral', hint: 'A removal case we are working on' },
  manual_report: { label: 'Manual Removal Report', tone: 'neutral', hint: 'A page you reported yourself' },
  authorization: { label: 'Authorization & Protection Setup', tone: 'neutral', hint: 'Signed letter, photos, protected pages' },
  billing: { label: 'Subscription & Billing', tone: 'neutral', hint: 'Plans, payments, invoices' },
  technical: { label: 'Technical Issue', tone: 'neutral', hint: 'Something is broken or will not load' },
  feature_request: { label: 'Feature Request', tone: 'neutral', hint: 'Something you would like us to build' },
  other: { label: 'Other', tone: 'neutral', hint: 'Anything else' },
};

/**
 * Why a priced search cost what it did. The stored values used to be these
 * words, so the chip printed a database key and /spend had a third copy of
 * them hardcoded beside it.
 */
export const SPEND_REASON: Readonly<Record<SpendReason, Term>> = {
  first_scan: { label: 'First scan', tone: 'progress' },
  renewal_window: { label: 'Renewal window', tone: 'attention' },
  steady_state: { label: 'Every other scan', tone: 'neutral' },
  unattributed: { label: 'Not part of a scan', tone: 'neutral' },
};

/** A metered allowance. One today; named rather than inlined. */
export const QUOTA_METRIC: Readonly<Record<QuotaMetric, Term>> = {
  on_demand_takedowns: { label: 'On-demand takedowns', tone: 'neutral' },
};

/**
 * Which channel a thread is on. Derived from `CHANNEL_LADDER`, which is also
 * the display order — a separate label table would eventually name the wrong
 * channel. Existed identically in `/cases` and `/leaks`.
 */
export function channelLabel(channel: string | null | undefined): string {
  if (!channel) return '—';
  return CHANNEL_LADDER.find((rung) => rung.key === channel)?.label ?? humanize(channel);
}

/**
 * Why a scan ran, for a client. A second table beside `SCAN_MODE` on purpose:
 * support reads "Live" (what kind of run), she reads "You asked for it" (why
 * it happened to her). Neither wording is right for the other reader.
 */
export const SCAN_MODE_CLIENT: Readonly<Record<ScanMode, Term>> = {
  scheduled: { label: 'Scheduled', tone: 'neutral' },
  live: { label: 'You asked for it', tone: 'progress' },
  first: { label: 'First search', tone: 'progress' },
};

/**
 * What happened to a URL somebody sent in themselves.
 *
 * The life of a submission in the five steps its sender cares about: we are
 * looking, we opened a case, the notice is out, it is gone, or we are not
 * acting. Why a submission was rejected is a line beside the chip rather than
 * a chip of its own — "no protected content found" and "your own page" are
 * the same outcome to the reader, and a colour for each turns a legible list
 * into a chart.
 *
 * `CASE_STATUS` stays the fuller vocabulary: a case has thirteen states and a
 * client watching one wants all of them. This is the summary a person who
 * pasted a link reads in a column three words wide.
 */
export const SUBMISSION_STATUS: Readonly<Record<SubmissionStatus, Term>> = {
  under_review: { label: 'Under review', tone: 'progress' },
  case_created: { label: 'Case created', tone: 'progress' },
  removal_in_progress: { label: 'Removal in progress', tone: 'progress' },
  removed: { label: 'Removed', tone: 'resolved' },
  rejected: { label: 'Rejected', tone: 'neutral' },
};
